export interface CommunityWithCommentView {
  communityView: CommunityView
  commentView: CommunityCommentView
}

export interface CommunityView {
  id: number
  category: CommunityCategory
  contents: {
    contents: string
    originContents: string
  }
  photos: string[]
  statistics: {
    viewCount: number
    thumbUpCount: number
    commentCount: number
    subscriberCount: number
  }
  writer: MemberView
  blindHistory: BlindHistoryView
  personalize: {
    hasThumbUp: boolean
    hasSubscribed: boolean
  }
  language: string
  createdAt: string
  updatedAt: string
}

export interface BlindHistoryView {
  id: number
  requesterName: string
  requestTime: string
  restoreRequestTime: string | null
  restoredTime: string | null
  restoreDeadlineTime: string | null
}

export interface CommunityCategory {
  categoryId: number
  categoryName: string
  categoryIconUrl: string
}

export interface ResponseOfSubscribeDocument {
  subscriberCount: number
  documentId: number
}

export interface CommunityCommentView {
  id: number
  communityId: number
  contents: {
    contents: string
    originContents: string
  }
  replyCommentId: number
  statistics: {
    thumbUpCount: number
  }
  callee: MemberView
  writer: MemberView
  blindHistory: BlindHistoryView
  personalize: {
    hasThumbUp: boolean
  }
  language: string
  deleted: boolean
  createdAt: string
  updatedAt: string
}

export interface DocumentCommentView {
  id: number
  createTime: number
  updateTime: number
  status: DocumentStatus
  calleeId: number | null
  calleeNickName: string | null
  calleeLevel: number | null
  calleeFake: boolean
  replyCommentId: number | null
  contents: string
  thumbUpCount: number
  commentCount: number
  reportCount: number
  edited: boolean
  isBest: boolean
  blindRestoreTime: number | null
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  lang: 'ko' | 'ja' | string | null
  translateResult: { contents: string } | null
  writerCountry: string
  hasThumbUp: boolean
  hasReported: boolean | null
  skipped: boolean | null
  writerNickName: string
  documentWriterId: number
  documentId: number
  writerId: number
  writerFake: boolean
  writerDoctorId: number | null
  calleeDoctorId: number | null
  writerLevel: number
  writerProfile: string
  writerName: string
  localTextFieldName: string
  blindRequestDTO: object | null
}

export interface MemberView {
  memberId: number
  doctor: MemberDoctorView | null
  type: MemberType
  country: string
  profileUrl: string
  nickName: string
  level: {
    level: number
    activist: boolean
    knowledgeTopPercent: number
    informTopPercent: number
    helpfulTopPercent: number
  }
  engagementType: string
}

export enum MemberType {
  USER = 'USER',
  DOCTOR = 'DOCTOR',
  ADMIN = 'ADMIN',
}

export enum DocumentStatus {
  /** 대기 */
  NOT_APPROVED,
  /** 승인 */
  APPROVED,
  /** 삭제 */
  DELETED,
  /** 임시숨김처리 */
  BLIND,
  /** 피스 (평화로운 처리, 작성자만 볼 수 있음) */
  PEACE,
  /** 승인요청 */
  APPROVE_REQ,
  /** 반려 */
  DENIED,
  /** 반려대기중 */
  DENY_WAITING,
}

export interface MemberDoctorView {
  memberId: number
  doctorId: number
  name: string
  hospitalId: number
  hospitalName: string
  typeName: string
}

export enum ContentsType {
  COMMUNITY = 'COMMUNITY',
  COMMUNITY_COMMENT = 'COMMUNITY_COMMENT',
  REVIEW = 'REVIEW',
  REVIEW_COMMENT = 'REVIEW_COMMENT',
  EVALUATION_COMMENT = 'EVALUATION_COMMENT',
}

export interface ReportCommunityContentParam {
  entityType: ReportEntityType
  entityId: number
  reason?: ReportReason
  memo?: string
  isSkip?: boolean
}

export interface PostCommunityDocumentParam {
  contents: string
  categoryId?: number
  photoUrls?: string
}

export interface PostCommunityDocumentResponse {
  id: number
  contents: string
  thumbnail: string | null
  photos: string[]
  thumbUpCount: number
  commentCount: number
  createTime: number
  updateTime: number
  writerId: number
  writerDoctorId: number | null
  writerNickName: string
  writerLevel: number
  writerProfile: string
  writerCountry: string
  writerStatus: string
  hasThumbUp: boolean
  category: {
    id: number
    createTime: number
    updateTime: number
    status: number
    icon: string | null
    name: string
    isForAdminWriter: boolean
  }
  categoryName: string
  categoryId: number
  status: number
  updatable: boolean
  hasReported: boolean
  reportCount: number
  subscriberCount: number
  viewCount: number
  hasDoctorComment: boolean | null
  skipped: null
  lang: string
  translateResult: null
  writerFake: boolean
  hasComment: boolean
  isNotice: boolean
  isBest: boolean
  hasSubscribed: boolean
  edited: boolean
  blindRequestDTO: null
  writer: {
    type: string
    id: number
  }
}

export interface PostCommunityCommentParam {
  documentId: number
  contents: string
  calleeId?: number
  replyCommentId?: number
}

export enum ReportEntityType {
  DOCUMENT = 'DOCUMENT',
  DOCU_COMMENT = 'DOCU_COMMENT',
}

export enum ReportReason {
  COMMERCIAL = 'COMMERCIAL',
  SEXUAL = 'SEXUAL',
  VIOLENCE = 'VIOLENCE',
  PRIVACY = 'PRIVACY',
  REPEAT = 'REPEAT',
  ETC = 'ETC',
}

export interface CommunityDocument {
  id: number
  contents: string
  photos: string[]
  thumbnail: string | null
  writerId: number
  writerNickName: string
  writerLevel: number
  writerCountry: string
  writerProfile: string
  writerStatus: 'NORMAL' | 'DELETED' | 'UNUSED' | 'INACTIVE'
  writerDoctorId: number | null
  writerEngagementType: null
  writerFake: boolean
  writerActivist: boolean
  hasThumbUp: boolean
  hasComment: boolean
  hasSubscribed: boolean
  subscriberCount: number
  commentCount: number
  thumbUpCount: number
  viewCount: number
  best: boolean
  notice: boolean
  edited: boolean
  hasDoctorComment: boolean
  category: {
    id: number
    name: string
  }
  categoryId: number
  categoryName: string
  lang: string
  translateResult: { contents: string } | null
  status: number
  createTime: number
  updateTime: number
}

export interface CommunityRecommendationsView {
  communityId: number
  contents: string
  totalCommentCount: number
  doctorCommentWritten: boolean
}
